import * as THREE from 'three';
import type { DamageField } from '../../structures/demolition/demolitionDamageField';

/** Matches Phase1 geometry fade so G-channel birth time and the time uniform share a clock. */
export const DAMAGE_FIELD_AGE_SECONDS = 24;

/**
 * Start considering a rebase before the 8-bit window saturates. The first
 * quantized G-shift applies once elapsed would exceed {@link DAMAGE_FIELD_AGE_SECONDS},
 * so fully-aged stamps can sit at age 1 (uniform=1, G=0) while later stamps
 * still have room to age.
 */
export const DAMAGE_FIELD_REBASE_SECONDS = 20;

export function isDamageFieldSamplingEnabled(
  search: string | URLSearchParams =
    typeof window === 'undefined' ? '' : window.location.search,
): boolean {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return params.get('damageField') !== '0';
}

/** Subtract `round(delta/24 * 255)` from every texel's G; leave R/B/A. */
export function rebaseDamageFieldBirth(field: DamageField, deltaSeconds: number): number {
  const shift = Math.round((deltaSeconds / DAMAGE_FIELD_AGE_SECONDS) * 255);
  if (shift <= 0) return 0;
  const stride = field.dimensions.channels;
  const data = field.data;
  for (let i = 1; i < data.length; i += stride) {
    const g = data[i];
    data[i] = g <= shift ? 0 : g - shift;
  }
  return shift;
}

/**
 * Windowed 8-bit birth clock. Uniform time is `(simTime - epoch) / 24` clamped
 * to 1. Rebase shifts epoch and every G so `age = now - birth` stays valid
 * past 24 sim-seconds instead of pinning at 0.
 */
export class DamageFieldClock {
  epoch = 0;

  normalize(simTime: number): number {
    if (!Number.isFinite(simTime)) return 0;
    return Math.min(1, Math.max(0, (simTime - this.epoch) / DAMAGE_FIELD_AGE_SECONDS));
  }

  maybeRebase(
    field: DamageField,
    texture: THREE.Data3DTexture | null,
    simTime: number,
  ): boolean {
    if (!Number.isFinite(simTime)) return false;
    const elapsed = simTime - this.epoch;
    if (elapsed <= DAMAGE_FIELD_REBASE_SECONDS) return false;
    // Consume whole-texel quanta of time past the 24s encoding window.
    // Between 20s and 24s this is a no-op so U can still reach 1.
    const overshoot = elapsed - DAMAGE_FIELD_AGE_SECONDS;
    if (overshoot <= 0) return false;
    const shift = Math.round((overshoot / DAMAGE_FIELD_AGE_SECONDS) * 255);
    if (shift <= 0) return false;
    const delta = (shift / 255) * DAMAGE_FIELD_AGE_SECONDS;
    rebaseDamageFieldBirth(field, delta);
    this.epoch += delta;
    if (texture) markDamageFieldTextureDirty(texture);
    return true;
  }
}

/**
 * Wrap the CPU RGBA8 volume. The texture aliases `field.data` — stamp in place,
 * then `markDamageFieldTextureDirty` / `uploadDamageFieldTextureIfDirty`.
 */
export function createDamageFieldTexture(field: DamageField): THREE.Data3DTexture {
  const { width, height, depth } = field.dimensions;
  const texture = new THREE.Data3DTexture(field.data, width, height, depth);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.wrapR = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  texture.userData.damageFieldDirty = false;
  return texture;
}

export function markDamageFieldTextureDirty(texture: THREE.Data3DTexture): void {
  texture.userData.damageFieldDirty = true;
}

/** At most one GPU upload per call (once per frame from the layer). */
export function uploadDamageFieldTextureIfDirty(texture: THREE.Data3DTexture): boolean {
  if (!texture.userData.damageFieldDirty) return false;
  texture.needsUpdate = true;
  texture.userData.damageFieldDirty = false;
  return true;
}
